import React, { useEffect, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, FormControlLabel,
  Checkbox, MenuItem, Tabs, Tab, Snackbar, Divider, Link,
} from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import api from '../../api/client';

interface Basemap {
  id: number; name: string; kind: string; url_template: string; attribution: string;
  min_zoom: number; max_zoom: number; max_native_zoom: number;
  wms_layers: string; wms_format: string; wms_transparent: boolean;
  is_default: boolean; is_active: boolean; sort_order: number;
}

const emptyForm = {
  name: '', kind: 'xyz', url_template: '', attribution: '',
  min_zoom: 0, max_zoom: 22, max_native_zoom: 19,
  wms_layers: '', wms_format: 'image/png', wms_transparent: true,
  is_default: false, is_active: true, sort_order: 100,
};

function BasemapsTab() {
  const [basemaps, setBasemaps] = useState<Basemap[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Basemap | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [toast, setToast] = useState('');

  const load = () => {
    api.get('/gis/basemaps', { params: { include_inactive: true } })
      .then((r) => setBasemaps(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const handleOpen = (b?: Basemap) => {
    if (b) {
      setEditing(b);
      setForm({
        name: b.name, kind: b.kind, url_template: b.url_template, attribution: b.attribution,
        min_zoom: b.min_zoom, max_zoom: b.max_zoom, max_native_zoom: b.max_native_zoom,
        wms_layers: b.wms_layers, wms_format: b.wms_format, wms_transparent: b.wms_transparent,
        is_default: b.is_default, is_active: b.is_active, sort_order: b.sort_order,
      });
    } else {
      setEditing(null);
      setForm(emptyForm);
    }
    setOpen(true);
  };

  const handleSave = async () => {
    try {
      if (editing) {
        await api.patch(`/gis/basemaps/${editing.id}`, form);
      } else {
        await api.post('/gis/basemaps', form);
      }
      setOpen(false);
      load();
    } catch {
      setToast('Save failed — check the URL template.');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this basemap? It will disappear from the map layer switcher.')) return;
    await api.delete(`/gis/basemaps/${id}`);
    load();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 700 }}>Custom Basemaps / Agency Imagery</Typography>
        <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Basemap</Button>
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Add an agency tile/service URL (XYZ / WMTS / tiled ArcGIS MapServer, or a WMS service) and it appears in the
        dispatch &amp; MDT map layer switcher alongside Street / Dark / Satellite. Mark one as the default to pre-select it.
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>URL</TableCell>
              <TableCell>Zoom</TableCell>
              <TableCell>Flags</TableCell>
              <TableCell width={100} align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {basemaps.map((b) => (
              <TableRow key={b.id}>
                <TableCell sx={{ fontWeight: 700 }}>{b.name}</TableCell>
                <TableCell><Chip size="small" label={b.kind.toUpperCase()} /></TableCell>
                <TableCell sx={{ maxWidth: 340, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: 12 }}>
                  {b.url_template}
                </TableCell>
                <TableCell>{b.min_zoom}–{b.max_zoom} (native {b.max_native_zoom})</TableCell>
                <TableCell>
                  {b.is_default ? <Chip size="small" color="primary" label="Default" sx={{ mr: 0.5 }} /> : null}
                  <Chip size="small" label={b.is_active ? 'Active' : 'Inactive'} color={b.is_active ? 'success' : 'default'} />
                </TableCell>
                <TableCell align="right">
                  <IconButton size="small" onClick={() => handleOpen(b)}><Edit fontSize="small" /></IconButton>
                  <IconButton size="small" color="error" onClick={() => handleDelete(b.id)}><Delete fontSize="small" /></IconButton>
                </TableCell>
              </TableRow>
            ))}
            {basemaps.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary', py: 4 }}>
                  No custom basemaps yet. Add one to use agency aerial imagery on the map.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Basemap' : 'Add Basemap'}</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth autoFocus label="Name" sx={{ mt: 1 }}
            value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="County Aerials 2024"
          />
          <TextField
            select fullWidth label="Type" sx={{ mt: 2 }}
            value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
          >
            <MenuItem value="xyz">XYZ / WMTS / tiled ArcGIS MapServer</MenuItem>
            <MenuItem value="wms">WMS service</MenuItem>
          </TextField>
          <TextField
            fullWidth label="URL Template" sx={{ mt: 2 }}
            value={form.url_template} onChange={(e) => setForm({ ...form, url_template: e.target.value })}
            placeholder={form.kind === 'wms'
              ? 'https://gis.example.gov/arcgis/services/Imagery/MapServer/WMSServer'
              : 'https://gis.example.gov/arcgis/rest/services/Imagery/MapServer/tile/{z}/{y}/{x}'}
            helperText={form.kind === 'wms'
              ? 'Base WMS endpoint (the layers/format are set below).'
              : 'Use {z}/{x}/{y} placeholders. Tiled ArcGIS MapServer uses {z}/{y}/{x}.'}
          />
          {form.kind === 'wms' && (
            <>
              <TextField
                fullWidth label="WMS Layers" sx={{ mt: 2 }}
                value={form.wms_layers} onChange={(e) => setForm({ ...form, wms_layers: e.target.value })}
                placeholder="0  (comma-separated layer names/ids)"
              />
              <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
                <TextField
                  label="WMS Format" sx={{ flex: 1 }}
                  value={form.wms_format} onChange={(e) => setForm({ ...form, wms_format: e.target.value })}
                />
                <FormControlLabel
                  control={<Checkbox checked={form.wms_transparent} onChange={(e) => setForm({ ...form, wms_transparent: e.target.checked })} />}
                  label="Transparent"
                />
              </Box>
            </>
          )}
          <TextField
            fullWidth label="Attribution" sx={{ mt: 2 }}
            value={form.attribution} onChange={(e) => setForm({ ...form, attribution: e.target.value })}
            placeholder="© Agency GIS"
          />
          <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
            <TextField
              type="number" label="Min Zoom" sx={{ flex: 1 }}
              value={form.min_zoom}
              onChange={(e) => setForm({ ...form, min_zoom: Number(e.target.value) || 0 })}
              slotProps={{ htmlInput: { min: 0, max: 24 } }}
            />
            <TextField
              type="number" label="Max Zoom" sx={{ flex: 1 }}
              value={form.max_zoom}
              onChange={(e) => setForm({ ...form, max_zoom: Number(e.target.value) || 0 })}
              slotProps={{ htmlInput: { min: 0, max: 24 } }}
            />
            <TextField
              type="number" label="Max Native Zoom" sx={{ flex: 1 }}
              value={form.max_native_zoom}
              onChange={(e) => setForm({ ...form, max_native_zoom: Number(e.target.value) || 0 })}
              slotProps={{ htmlInput: { min: 0, max: 24 } }}
            />
          </Box>
          <Divider sx={{ my: 2 }} />
          <FormControlLabel
            control={<Checkbox checked={form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} />}
            label="Default basemap (pre-selected on load)"
          />
          <FormControlLabel
            control={<Checkbox checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} />}
            label="Active (shown in the map switcher)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={!form.name.trim() || !form.url_template.trim()}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={3500} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}

interface Preview { feature_count: number; fields: string[]; geometry_types: string[]; }
interface DatasetStats { address_points: number; centerlines: number; geocode_source: string; }

type MapKey = string;

// A field the admin maps from the source dataset's attributes.
interface FieldDef { key: MapKey; label: string; help?: string; }

const AP_FIELDS: FieldDef[] = [
  { key: 'full_address_field', label: 'Full address field', help: 'e.g. FULLADDR — use this OR house + street below' },
  { key: 'house_field', label: 'House number field', help: 'e.g. ADD_NUM' },
  { key: 'street_field', label: 'Street name field', help: 'e.g. ST_NAME (incl. prefix/type/suffix)' },
  { key: 'city_field', label: 'City field' },
  { key: 'state_field', label: 'State field' },
  { key: 'zip_field', label: 'ZIP field' },
];

const CL_FIELDS: FieldDef[] = [
  { key: 'street_field', label: 'Street name field', help: 'e.g. FULLNAME / ST_NAME' },
  { key: 'l_from_field', label: 'Left from #', help: 'Low house # on the left side' },
  { key: 'l_to_field', label: 'Left to #' },
  { key: 'r_from_field', label: 'Right from #' },
  { key: 'r_to_field', label: 'Right to #' },
  { key: 'city_field', label: 'City field' },
  { key: 'state_field', label: 'State field' },
  { key: 'zip_field', label: 'ZIP field' },
];

function ImportDialog(props: {
  open: boolean; onClose: () => void; onDone: () => void;
  title: string; base: string; fields: FieldDef[];
}) {
  const { open, onClose, onDone, title, base, fields } = props;
  const [srcType, setSrcType] = useState<'file' | 'esri'>('file');
  const [file, setFile] = useState<File | null>(null);
  const [url, setUrl] = useState('');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [fixedCity, setFixedCity] = useState('');
  const [fixedState, setFixedState] = useState('');
  const [replaceExisting, setReplaceExisting] = useState(true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [result, setResult] = useState('');

  const reset = () => {
    setSrcType('file'); setFile(null); setUrl(''); setPreview(null);
    setMapping({}); setFixedCity(''); setFixedState(''); setReplaceExisting(true);
    setErr(''); setResult('');
  };

  const loadPreview = async () => {
    setErr(''); setResult(''); setBusy(true);
    try {
      if (srcType === 'file') {
        if (!file) { setErr('Choose a file first.'); setBusy(false); return; }
        const fd = new FormData();
        fd.append('file', file);
        const r = await api.post(`${base}/import/preview`, fd);
        setPreview(r.data);
      } else {
        if (!url.trim()) { setErr('Enter an ArcGIS service URL.'); setBusy(false); return; }
        const r = await api.post(`${base}/esri-preview`, { url });
        setPreview(r.data);
      }
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Could not read that source.');
    } finally { setBusy(false); }
  };

  const doImport = async () => {
    setErr(''); setBusy(true);
    try {
      const payload: Record<string, unknown> = {
        ...mapping, fixed_city: fixedCity, fixed_state: fixedState,
        replace_existing: replaceExisting,
      };
      let r;
      if (srcType === 'file' && file) {
        const fd = new FormData();
        fd.append('file', file);
        fd.append('mapping', JSON.stringify(payload));
        r = await api.post(`${base}/import`, fd);
      } else {
        r = await api.post(`${base}/import-esri`, { ...payload, url });
      }
      const d = r.data;
      setResult(`Imported ${d.inserted}, skipped ${d.skipped}.` + (d.errors?.length ? ` First error: ${d.errors[0]}` : ''));
      onDone();
    } catch (e: any) {
      setErr(e?.response?.data?.detail || 'Import failed.');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onClose={() => { reset(); onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <TextField select fullWidth label="Source" sx={{ mt: 1 }} value={srcType}
          onChange={(e) => { setSrcType(e.target.value as 'file' | 'esri'); setPreview(null); }}>
          <MenuItem value="file">Upload file (GeoJSON or zipped Shapefile)</MenuItem>
          <MenuItem value="esri">ArcGIS Feature Service URL</MenuItem>
        </TextField>
        {srcType === 'file' ? (
          <Button component="label" variant="outlined" sx={{ mt: 2 }}>
            {file ? file.name : 'Choose file (.geojson / .json / .zip)'}
            <input hidden type="file" accept=".geojson,.json,.zip"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setPreview(null); }} />
          </Button>
        ) : (
          <TextField fullWidth label="ArcGIS service URL" sx={{ mt: 2 }} value={url}
            onChange={(e) => { setUrl(e.target.value); setPreview(null); }}
            placeholder="https://gis.example.gov/arcgis/rest/services/AddressPoints/FeatureServer/0" />
        )}
        <Button variant="outlined" sx={{ mt: 2 }} onClick={loadPreview} disabled={busy}>Load preview</Button>

        {preview && (
          <>
            <Typography variant="body2" sx={{ mt: 2 }} color="text.secondary">
              {preview.feature_count} features · geometry: {preview.geometry_types.join(', ') || 'none'}
            </Typography>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Map attribute fields</Typography>
            {fields.map((f) => (
              <TextField key={f.key} select fullWidth label={f.label} sx={{ mt: 1.5 }}
                helperText={f.help} value={mapping[f.key] || ''}
                onChange={(e) => setMapping({ ...mapping, [f.key]: e.target.value })}>
                <MenuItem value="">(none)</MenuItem>
                {preview.fields.map((pf) => <MenuItem key={pf} value={pf}>{pf}</MenuItem>)}
              </TextField>
            ))}
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <TextField label="Default City (if no field)" sx={{ flex: 1 }} value={fixedCity}
                onChange={(e) => setFixedCity(e.target.value)} />
              <TextField label="Default State (if no field)" sx={{ flex: 1 }} value={fixedState}
                onChange={(e) => setFixedState(e.target.value)} />
            </Box>
            <FormControlLabel sx={{ mt: 1 }}
              control={<Checkbox checked={replaceExisting} onChange={(e) => setReplaceExisting(e.target.checked)} />}
              label="Replace all existing records in this dataset" />
          </>
        )}
        {err && <Typography color="error" variant="body2" sx={{ mt: 2 }}>{err}</Typography>}
        {result && <Typography color="success.main" variant="body2" sx={{ mt: 2 }}>{result}</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => { reset(); onClose(); }}>Close</Button>
        <Button variant="contained" onClick={doImport} disabled={busy || !preview}>Import</Button>
      </DialogActions>
    </Dialog>
  );
}

function GeocodingTab() {
  const [stats, setStats] = useState<DatasetStats>({ address_points: 0, centerlines: 0, geocode_source: 'osm' });
  const [apOpen, setApOpen] = useState(false);
  const [clOpen, setClOpen] = useState(false);
  const [toast, setToast] = useState('');

  const load = () => { api.get('/gis/datasets').then((r) => setStats(r.data)).catch(() => {}); };
  useEffect(() => { load(); }, []);

  const setPolicy = async (source: string) => {
    try {
      const r = await api.patch('/gis/geocode-policy', { geocode_source: source });
      setStats(r.data);
      setToast('Geocoding policy updated.');
    } catch { setToast('Could not update policy.'); }
  };

  const clear = async (which: 'address-points' | 'centerlines') => {
    if (!window.confirm(`Delete ALL ${which.replace('-', ' ')}? This cannot be undone.`)) return;
    await api.delete(`/gis/${which}`);
    load();
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Geocoding Source</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Choose how addresses are geocoded for New Call, MDT, and drag-pin/reverse lookups. Agency data uses your
        imported address points first (exact match wins), then street-centerline interpolation.
      </Typography>
      <TextField select label="Geocoding policy" sx={{ minWidth: 340 }} value={stats.geocode_source}
        onChange={(e) => setPolicy(e.target.value)}>
        <MenuItem value="osm">OpenStreetMap / Nominatim only (default)</MenuItem>
        <MenuItem value="agency_then_osm">Agency data, then OSM fallback (recommended)</MenuItem>
        <MenuItem value="agency_only">Agency data only (no OSM fallback)</MenuItem>
      </TextField>

      <Divider sx={{ my: 3 }} />

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <Paper sx={{ p: 2, flex: 1, minWidth: 280 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Address Points</Typography>
            <Chip label={`${stats.address_points} loaded`} color={stats.address_points ? 'success' : 'default'} size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
            One point per structure/parcel with a civic address. An exact match geocodes with full confidence.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={() => setApOpen(true)}>Import</Button>
            <Button variant="outlined" size="small" color="error" disabled={!stats.address_points}
              onClick={() => clear('address-points')}>Clear</Button>
          </Box>
        </Paper>

        <Paper sx={{ p: 2, flex: 1, minWidth: 280 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Street Centerlines</Typography>
            <Chip label={`${stats.centerlines} loaded`} color={stats.centerlines ? 'success' : 'default'} size="small" />
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ my: 1 }}>
            Road segments with left/right address ranges, used to interpolate a point when no address point exists.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={() => setClOpen(true)}>Import</Button>
            <Button variant="outlined" size="small" color="error" disabled={!stats.centerlines}
              onClick={() => clear('centerlines')}>Clear</Button>
          </Box>
        </Paper>
      </Box>

      <ImportDialog open={apOpen} onClose={() => setApOpen(false)} onDone={load}
        title="Import Address Points" base="/gis/address-points" fields={AP_FIELDS} />
      <ImportDialog open={clOpen} onClose={() => setClOpen(false)} onDone={load}
        title="Import Street Centerlines" base="/gis/centerlines" fields={CL_FIELDS} />
      <Snackbar open={!!toast} autoHideDuration={3000} onClose={() => setToast('')} message={toast} />
    </Box>
  );
}

export default function GisPage() {
  const [tab, setTab] = useState(0);
  return (
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>GIS / Mapping</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Use agency GIS data — custom basemap imagery, address points, and street centerlines — in place of
        OpenStreetMap. See the{' '}
        <Link href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">basemap provider terms</Link>{' '}
        before publishing.
      </Typography>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Basemaps" />
        <Tab label="Geocoding" />
      </Tabs>
      {tab === 0 && <BasemapsTab />}
      {tab === 1 && <GeocodingTab />}
    </Box>
  );
}
